/**
 * Mobile Bottom Navigation Bar
 * Sticky bottom nav for mobile — thumb-friendly, 44px+ tap targets
 */
import { memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Briefcase, Crown, Calculator, User } from 'lucide-react';

function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();

  const tabs = [
    { label: 'Home',      icon: Home,        path: '/' },
    { label: 'Jobs',      icon: Briefcase,   path: '/jobs' },
    { label: 'Invest',    icon: Crown,       path: '/crown-pool' },
    { label: 'Estimator', icon: Calculator,  path: '/free-estimator' },
    { label: user ? 'Profile' : 'Sign In', icon: User, path: user ? '/profile' : '/login' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
      <div className="flex items-stretch h-16">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);
          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={`flex-1 flex flex-col items-center justify-center gap-1 min-h-[44px] transition-colors
                ${active
                  ? 'text-emerald-600'
                  : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              {/* Crown Pool special highlight */}
              {tab.path === '/crown-pool' ? (
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all
                  ${active
                    ? 'bg-amber-500 shadow-amber-300'
                    : 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-amber-200'
                  }`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
              ) : (
                <div className={`w-8 h-8 flex items-center justify-center rounded-xl transition-all
                  ${active ? 'bg-emerald-50' : ''}`}>
                  <Icon className={`w-5 h-5 ${active ? 'text-emerald-600' : 'text-gray-400'}`} />
                </div>
              )}
              <span className={`text-[10px] font-semibold leading-none
                ${tab.path === '/crown-pool' ? (active ? 'text-amber-600' : 'text-amber-500') : ''}`}>
                {tab.label}
              </span>
              {active && tab.path !== '/crown-pool' && (
                <span className="absolute bottom-1 w-1 h-1 rounded-full bg-emerald-500" />
              )}
            </button>
          );
        })}
      </div>
      {/* Safe area spacer for iOS */}
      <div className="h-safe-area-inset-bottom bg-white" />
    </nav>
  );
}

export default memo(MobileBottomNav);
